import type { MessageKey } from '@dijitalapp/i18n';
import {
  NOTIFICATION_PREVIEWS,
  type Audience,
  type DeviceDto,
  type NotificationPreview,
  type PasskeyDto,
  type PrivacyDto,
  type PrivacySettings,
} from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { E2eeSettings } from '../components/E2eeUi';
import { Icon, type IconName } from '../components/Icon';
import { PageHeader } from '../components/SectionNav';
import { api, errorMessageKey } from '../lib/api';
import { useApp, type ThemePreference } from '../lib/app-context';
import { PasskeyCancelledError, passkeysSupported, registerPasskey, suggestPasskeyName } from '../lib/passkeys';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push';
import { useShell } from './Shell';

const BOOLEAN_SETTINGS: { key: keyof PrivacySettings; label: MessageKey }[] = [
  { key: 'profileDiscoverable', label: 'privacy.profileDiscoverable' },
  { key: 'dmFromNonContacts', label: 'privacy.dmFromNonContacts' },
  { key: 'phoneVisible', label: 'privacy.phoneVisible' },
  { key: 'readReceipts', label: 'privacy.readReceipts' },
];
const AUDIENCES: Audience[] = ['everyone', 'contacts', 'nobody'];

function PrivacySection() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [data, setData] = useState<PrivacyDto | null>(null);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    void api<PrivacyDto>('GET', '/me/privacy').then(setData);
  }, []);

  const update = async (patch: Partial<PrivacySettings>) => {
    try {
      setData(await api<PrivacyDto>('PATCH', '/me/privacy', patch));
      setStatus({ kind: 'ok', text: t('privacy.saved') });
    } catch (err) {
      setStatus({ kind: 'error', text: t(errorMessageKey(err)) });
    }
  };

  if (!data) return <p className="muted">{t('app.loading')}</p>;
  return (
    <section className="card settings-list" aria-labelledby="privacy-title">
      <h2 id="privacy-title" className="visually-hidden">
        {t('settings.privacy')}
      </h2>
      {BOOLEAN_SETTINGS.map(({ key, label }) => {
        const locked = data.locked.includes(key);
        return (
          <label key={key} className="toggle-row">
            <span>
              {t(label)}
              {locked && (
                <span className="hint" style={{ display: 'block' }}>
                  {t('privacy.lockedByAge')}
                </span>
              )}
            </span>
            <span className="spacer" />
            <input
              type="checkbox"
              role="switch"
              checked={Boolean(data.settings[key])}
              disabled={locked}
              onChange={(e) => void update({ [key]: e.target.checked })}
              style={{ width: 22, height: 22 }}
            />
          </label>
        );
      })}
      <div className="toggle-row">
        <label htmlFor="group-add">{t('privacy.groupAdd')}</label>
        <span className="spacer" />
        <select
          id="group-add"
          className="input"
          style={{ width: 'auto' }}
          value={data.settings.groupAdd}
          onChange={(e) => void update({ groupAdd: e.target.value as Audience })}
        >
          {AUDIENCES.filter((a) => a !== 'everyone' || !data.locked.includes('profileDiscoverable')).map((a) => (
            <option key={a} value={a}>
              {t(`privacy.audience.${a}` as MessageKey)}
            </option>
          ))}
        </select>
      </div>
      <div className="toggle-row">
        <label htmlFor="last-seen">{t('privacy.lastSeen')}</label>
        <span className="spacer" />
        <select
          id="last-seen"
          className="input"
          style={{ width: 'auto' }}
          value={data.settings.lastSeen}
          onChange={(e) => void update({ lastSeen: e.target.value as Audience })}
        >
          {AUDIENCES.map((a) => (
            <option key={a} value={a}>
              {t(`privacy.audience.${a}` as MessageKey)}
            </option>
          ))}
        </select>
      </div>
      {status && (
        <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
          {status.text}
        </p>
      )}
    </section>
  );
}

function DevicesSection() {
  const { i18n, setMe } = useApp();
  const { t, formatDate } = i18n;
  const navigate = useNavigate();
  const [devices, setDevices] = useState<DeviceDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDevices = () => api<{ devices: DeviceDto[] }>('GET', '/devices').then((r) => r.devices);
  const load = async () => setDevices(await fetchDevices());
  useEffect(() => {
    void fetchDevices().then(setDevices);
  }, []);

  const act = async (fn: () => Promise<unknown>, signedOutSelf = false) => {
    try {
      await fn();
      if (signedOutSelf) {
        setMe(null);
        navigate('/login', { replace: true });
      } else await load();
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  if (!devices) return <p className="muted">{t('app.loading')}</p>;
  return (
    <section className="card stack" aria-labelledby="devices-title">
      <div className="row">
        <h2 id="devices-title" className="card-title">
          {t('settings.devices')}
        </h2>
        <span className="badge">{t('devices.count', { count: devices.length })}</span>
      </div>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      {devices.map((d) => (
        <div key={d.id} className="toggle-row">
          <div style={{ minWidth: 0 }}>
            <strong>{d.name}</strong> {d.current && <span className="badge">{t('devices.current')}</span>}
            <p className="muted">{t('devices.lastActive', { date: formatDate(d.lastActiveAt) })}</p>
            <p className="muted">{t('devices.firstSeen', { date: formatDate(d.firstSeenAt) })}</p>
          </div>
          <span className="spacer" />
          <button className="btn btn-sm btn-danger" type="button" onClick={() => void act(() => api('DELETE', `/devices/${d.id}`), d.current)}>
            {t('devices.signOut')}
          </button>
        </div>
      ))}
      {devices.length > 1 && (
        <button className="btn btn-danger" type="button" onClick={() => void act(() => api('POST', '/devices/revoke-others'))}>
          {t('devices.signOutOthers')}
        </button>
      )}
    </section>
  );
}

const PUSH_STATE_TEXT: Record<Exclude<PushState, 'off'>, MessageKey> = {
  on: 'notifications.enabled',
  denied: 'notifications.denied',
  unsupported: 'notifications.unsupported',
  unavailable: 'notifications.unavailable',
};

function NotificationsSection() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [state, setState] = useState<PushState | null>(null);
  const [preview, setPreview] = useState<NotificationPreview>('sender');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void pushState().then(setState, () => setState('unavailable'));
    void api<{ preview: NotificationPreview }>('GET', '/me/notifications').then((r) => setPreview(r.preview));
  }, []);

  const run = async (fn: () => Promise<PushState>) => {
    setError(null);
    try {
      setState(await fn());
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const changePreview = async (value: NotificationPreview) => {
    setPreview(value);
    try {
      await api('PATCH', '/me/notifications', { preview: value });
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <section className="card stack" aria-labelledby="notifications-title">
      <h2 id="notifications-title" className="card-title">
        {t('notifications.title')}
      </h2>
      <p className="muted">{t('notifications.body')}</p>
      {state && state !== 'off' && <p role="status">{t(PUSH_STATE_TEXT[state])}</p>}
      {state === 'off' && (
        <button className="btn btn-primary" type="button" onClick={() => void run(enablePush)}>
          {t('notifications.enable')}
        </button>
      )}
      {state === 'on' && (
        <button className="btn" type="button" onClick={() => void run(disablePush)}>
          {t('notifications.disable')}
        </button>
      )}
      <div className="field">
        <label htmlFor="notification-preview">{t('notifications.preview')}</label>
        <select
          id="notification-preview"
          className="input"
          value={preview}
          aria-describedby="notification-preview-hint"
          onChange={(e) => void changePreview(e.target.value as NotificationPreview)}
        >
          {NOTIFICATION_PREVIEWS.map((p) => (
            <option key={p} value={p}>
              {t(`notifications.preview.${p}` as MessageKey)}
            </option>
          ))}
        </select>
        <span className="hint" id="notification-preview-hint">
          {t('notifications.previewHint')}
        </span>
      </div>
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

const fetchPasskeys = () => api<{ passkeys: PasskeyDto[] }>('GET', '/passkeys').then((r) => r.passkeys);

function PasskeysSection() {
  const { i18n, config } = useApp();
  const { t, formatDate } = i18n;
  const [passkeys, setPasskeys] = useState<PasskeyDto[] | null>(null);
  const [supported, setSupported] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    void fetchPasskeys().then(setPasskeys);
    void passkeysSupported().then(setSupported, () => setSupported(false));
  }, []);

  const run = async (fn: () => Promise<unknown>, okText?: string) => {
    setStatus(null);
    try {
      await fn();
      setPasskeys(await fetchPasskeys());
      if (okText) setStatus({ kind: 'ok', text: okText });
    } catch (err) {
      const text = err instanceof PasskeyCancelledError ? t('auth.passkey.cancelled') : t(errorMessageKey(err));
      setStatus({ kind: 'error', text });
    }
  };

  if (!config?.authMethods.passkey) return null;
  return (
    <section className="card stack" aria-labelledby="passkeys-title">
      <h2 id="passkeys-title" className="card-title">
        {t('passkeys.title')}
      </h2>
      <p className="muted">{t('passkeys.body')}</p>
      {passkeys?.length === 0 && <p className="muted">{t('passkeys.empty')}</p>}
      {passkeys?.map((p) => (
        <div key={p.id} className="toggle-row">
          <div style={{ minWidth: 0 }}>
            <strong>{p.name}</strong> {p.deviceType === 'multiDevice' && <span className="badge">{t('passkeys.synced')}</span>}
            <p className="muted">{t('passkeys.created', { date: formatDate(p.createdAt) })}</p>
            {p.lastUsedAt && <p className="muted">{t('passkeys.lastUsed', { date: formatDate(p.lastUsedAt) })}</p>}
          </div>
          <span className="spacer" />
          <button className="btn btn-sm btn-danger" type="button" onClick={() => void run(() => api('DELETE', `/passkeys/${p.id}`))}>
            {t('passkeys.remove')}
          </button>
        </div>
      ))}
      {supported ? (
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => void run(() => registerPasskey(suggestPasskeyName()), t('passkeys.added'))}
        >
          {t('passkeys.add')}
        </button>
      ) : (
        <p className="hint">{t('auth.passkey.unsupported')}</p>
      )}
      {status && (
        <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
          {status.text}
        </p>
      )}
    </section>
  );
}

export const SETTINGS_SECTIONS = ['account', 'appearance', 'privacy', 'notifications', 'security', 'devices'] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];
export const SETTINGS_ICONS: Record<SettingsSection, IconName> = {
  account: 'user',
  appearance: 'palette',
  privacy: 'shield',
  notifications: 'bell',
  security: 'key',
  devices: 'monitor',
};

function useLogout() {
  const { setMe } = useApp();
  const navigate = useNavigate();
  return async () => {
    await api('POST', '/auth/logout').catch(() => undefined);
    setMe(null);
    navigate('/login', { replace: true });
  };
}

function AccountSection() {
  const { i18n, me } = useApp();
  const { t, formatDate } = i18n;
  const logout = useLogout();
  if (!me) return null;
  return (
    <>
      <section className="card profile-card" aria-label={t('settings.section.account')}>
        <span className="avatar avatar-lg" aria-hidden>
          {Array.from(me.displayName.trim())[0]?.toUpperCase() ?? '?'}
        </span>
        <div className="profile-text">
          <strong className="profile-name">{me.displayName}</strong>
          <span className="muted">@{me.username}</span>
        </div>
      </section>
      <section className="card" aria-labelledby="account-details">
        <h2 id="account-details" className="card-title">
          {t('settings.account.details')}
        </h2>
        <dl className="detail-list">
          <div>
            <dt>{t('settings.account.email')}</dt>
            <dd>
              {me.email}{' '}
              <span className={`badge ${me.emailVerified ? 'badge-success' : 'badge-warning'}`}>
                {me.emailVerified ? t('settings.account.verified') : t('settings.account.unverified')}
              </span>
            </dd>
          </div>
          <div>
            <dt>{t('settings.account.type')}</dt>
            <dd>{me.ageBand === 'adult' ? t('settings.account.adult') : t('settings.account.minor')}</dd>
          </div>
          <div>
            <dt>{t('settings.account.since')}</dt>
            <dd>{formatDate(me.createdAt)}</dd>
          </div>
        </dl>
      </section>
      <section className="card" aria-labelledby="account-session">
        <h2 id="account-session" className="card-title">
          {t('settings.account.session')}
        </h2>
        <p className="muted">{t('settings.account.sessionHint')}</p>
        <div className="card-actions">
          <button className="btn" type="button" onClick={() => void logout()}>
            <Icon name="logout" size={18} /> {t('auth.logout')}
          </button>
        </div>
      </section>
    </>
  );
}

function AppearanceSection() {
  const { i18n, theme, setTheme, setLocale } = useApp();
  const { t } = i18n;
  return (
    <section className="card stack" aria-labelledby="appearance-title">
      <h2 id="appearance-title" className="card-title">
        {t('settings.theme')}
      </h2>
      <div className="segmented" role="radiogroup" aria-label={t('settings.theme')}>
        {(['light', 'dark', 'system'] as ThemePreference[]).map((th) => (
          <button key={th} type="button" role="radio" aria-checked={theme === th} onClick={() => setTheme(th)}>
            {t(`settings.theme.${th}` as MessageKey)}
          </button>
        ))}
      </div>
      <div className="field">
        <label htmlFor="lang">{t('settings.language')}</label>
        <select id="lang" className="input" value={i18n.locale} onChange={(e) => setLocale(e.target.value as 'tr' | 'en')}>
          <option value="tr">Türkçe</option>
          <option value="en">English</option>
        </select>
      </div>
    </section>
  );
}

/** Settings: one section at a time; the section list lives in the middle pane (see Shell). */
export function SettingsPage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { section: param } = useParams();
  const { setMobileView } = useShell();
  const section: SettingsSection = (SETTINGS_SECTIONS as readonly string[]).includes(param ?? '') ? (param as SettingsSection) : 'account';

  return (
    <div className="content-inner">
      <PageHeader
        title={t(`settings.section.${section}` as MessageKey)}
        description={t(`settings.section.${section}.desc` as MessageKey)}
        back={() => setMobileView('list')}
      />
      {section === 'account' && <AccountSection />}
      {section === 'appearance' && <AppearanceSection />}
      {section === 'privacy' && <PrivacySection />}
      {section === 'notifications' && <NotificationsSection />}
      {section === 'security' && (
        <>
          <PasskeysSection />
          <E2eeSettings />
        </>
      )}
      {section === 'devices' && <DevicesSection />}
    </div>
  );
}
